import { y } from '@circlefin/form'
import { schema } from '@features/bank-account/forms/Dropdown'
import { isRailType } from '@services/type-guards/BankAccount'
import { currencyToToken, tokenToCurrency } from '@shared/components/common'
import { Currency, RailType, WireTransferTypeEnum } from '@shared/graphql'

import type { BankAccount, BankAccountType } from '@shared/graphql'

export type Stablecoin = Currency.USDC | Currency.EURC

// All currencies that are supported for FX transfers by stablecoin.
export const DEFAULT_FX_CURRENCY_OPTIONS: Record<Stablecoin, Currency[]> = {
  [Currency.USDC]: [Currency.USD, Currency.MXN, Currency.BRL],
  [Currency.EURC]: [Currency.EUR],
}

/**
 * Get the default currency options for the given currency.
 */
export const getDefaultCurrencyOptions = (currency: Currency) => {
  if (currency === Currency.USDC) {
    return DEFAULT_FX_CURRENCY_OPTIONS[Currency.USDC]
  }

  return DEFAULT_FX_CURRENCY_OPTIONS[Currency.EURC]
}

interface GetDefaultRailProps {
  /**
   * The bank account we wish to get the default for.
   */
  account?: BankAccount
  /**
   * A boolean representing whether or not we are in the deposit flow.
   * (as opposed to the withdrawal flow).
   */
  isDepositMode?: boolean
  /**
   * The currency that the user would like to receive or redeem. Fixed based on prior selection.
   */
  currency: Currency
}

/**
 * Retrieve the default rail for the given account and fixed currency.
 *
 * If we are in deposit mode and the account supports the wire rail we return the wire
 * rail as this is the most general rail option. Otherwise we return the first eligible
 * rail from the account.
 */
export const getDefaultRail = ({
  account,
  isDepositMode,
  currency,
}: GetDefaultRailProps) => {
  if (!account) return ''

  if (
    isDepositMode &&
    'transferTypes' in account &&
    account.transferTypes?.some((rail) => rail?.type === RailType.wire)
  ) {
    return RailType.wire
  }

  return getEligibleRails({
    bankAccount: account,
    currency,
  })[0]
}

/**
 * Retrieve the eligible rails for the given bank account and fixed currency.
 *
 * If the bank account is not provided we return an empty array.
 * If the bank account does not have a `transferTypes` property we return the bank account type.
 * Otherwise we filter the transfer types by the fixed currency and return the first eligible rail.
 *
 * Rails are only eligible if they support the equivalent currency of the fixed currency.
 * For example, if the fixed currency is USDC then we only return rails that support USD.
 */
export const getEligibleRails = ({
  bankAccount,
  currency,
}: {
  bankAccount: BankAccount | undefined
  currency?: Currency
}): Array<RailType | BankAccountType> => {
  if (bankAccount == null || currency == null) {
    return []
  }

  if (!('transferTypes' in bankAccount) || !bankAccount.transferTypes) {
    return [bankAccount.type]
  }

  const fiatCurrency = tokenToCurrency(currency)

  const filteredRails = bankAccount.transferTypes
    // Filter the rails to only include those that support the fixed currency fiat equivalent.
    .filter((rail) => Boolean(rail?.currencies.includes(fiatCurrency)))
    .map((rail) => rail?.type)
    .filter((type) => type !== undefined)

  return filteredRails.length > 0 ? filteredRails : [bankAccount.type]
}

interface GetCurrenciesAndDefault {
  /**
   * A rail key. This is usually whatever new rail was just selected in the UI for which
   * we want to generate eligible fiat currencies for.
   */
  newRail: RailType | BankAccountType
  /**
   * A bank account. This could be wire type or non wire type.
   * In the event it is wire type we use the given rail to determine what fiat currencies are supported.
   */
  bankAccount: BankAccount
  /**
   * The currency that the user would like to receive or redeem. Fixed based on prior selection.
   */
  currency: Currency
}

/**
 * This function returns a list of currencies an account supports as well as a default option to start with.
 * This corresponds to the fiat currency dropdown selector in the UI and ensure it starts off with a valid default currency.
 */
export const getCurrenciesAndDefault = ({
  newRail,
  bankAccount,
  currency,
}: GetCurrenciesAndDefault) => {
  const defaultOptions = {
    options: getDefaultCurrencyOptions(currency),
    default: getDefaultCurrencyOptions(currency)[0],
  }

  if (!('transferTypes' in bankAccount) || !bankAccount.transferTypes) {
    return defaultOptions
  }

  const relevantRailInfo = bankAccount.transferTypes.find(
    (railInfo) => railInfo?.type != null && railInfo.type === newRail,
  )

  if (!relevantRailInfo) {
    return defaultOptions
  }

  // Filter the currencies to exclude unsupported currencies.
  const filteredCurrencies = relevantRailInfo.currencies.filter((currency) =>
    defaultOptions.options.includes(currency),
  )

  return {
    options: filteredCurrencies,
    default: filteredCurrencies[0],
  }
}

interface ValidateRailTransferLimitsProps {
  /**
   * Amount string that represents the current user input we are validating against any existing transfer limits.
   */
  amount: string
  /**
   * Rail that is currently selected in the form.
   */
  rail: string
  /**
   * Bank account that is currently selected in the form.
   */
  account: BankAccount | undefined
  /**
   * Fiat currency currently selected in the form.
   */
  currency: Currency
}

/**
 * This function takes in an amount, rail, account and currency and returns a boolean.
 * If the rail is not supported by transfer limits we return true.
 * If the rail is supported we check the amount against the limits and return true if the amount is within the limits.
 */
export const validateRailTransferLimits = ({
  amount,
  rail,
  account,
  currency,
}: ValidateRailTransferLimitsProps): boolean => {
  if (!isRailType(rail)) {
    return true
  }

  const transferType =
    account && 'transferTypes' in account
      ? account.transferTypes?.find((tt) => tt?.type === rail)
      : undefined

  // Check against all transfer limits for the selected currency.
  return (
    transferType?.transactionLimits?.every((lim) =>
      lim.currency === currency ? Number(lim.limit) >= Number(amount) : true,
    ) ?? true
  )
}

/**
 * Yup validation logic used in the FX form to check amount inputs against any possible rail-specific
 * transfer limits that may exist for a single transaction. This only applies to withdrawals as deposits do not have
 * rails.
 */
export const getRailTransferLimitsValidation = (
  amountSchema: y.StringSchema<string, y.AnyObject, undefined, ''>,
) =>
  amountSchema.test({
    name: 'exceedsTransactionTypeLimit',
    test: (value, context) => {
      const formValues = context.parent as FxFormValues
      return validateRailTransferLimits({
        amount: value,
        rail: formValues.rail,
        account: formValues.account,
        currency: formValues.toCurrency,
      })
    },
  })

/**
 * Function to validate that the amount entered by the user does not exceed the remaining limit.
 */
export const validateRemainingLimit =
  (remainingLimit: string | undefined) =>
  (amount: string): boolean => {
    return (
      remainingLimit === undefined || Number(remainingLimit) >= Number(amount)
    )
  }

/**
 * Yup validation logic used in the FX form to check amount inputs against the remaining limit.
 */
export const getRemainingLimitValidation =
  (remainingLimit: string | undefined) =>
  (amountSchema: y.StringSchema<string, y.AnyObject, undefined, ''>) =>
    amountSchema.test({
      name: 'exceedsRemainingLimit',
      exclusive: false,
      test: validateRemainingLimit(remainingLimit),
    })

export const basicSchema = {
  mode: y.string().oneOf(Object.values(WireTransferTypeEnum)).notRequired(),
  fromAmount: y.string().required(),
  fromCurrency: y.mixed<Currency>().oneOf(Object.values(Currency)).required(),
  toAmount: y.string().required(),
  toCurrency: y.mixed<Currency>().oneOf(Object.values(Currency)).required(),
  account: y.mixed<BankAccount>(),
  rail: y.string().required(),
  checkbox: y.boolean().when(['mode', 'fromCurrency', 'toCurrency'], {
    is: (mode: string, fromCurrency: Currency, toCurrency: Currency) => {
      return mode === 'deposit' && !isSameCurrency(fromCurrency, toCurrency)
    },
    then: (schema) => schema.oneOf([true]).required(),
    otherwise: (schema) => schema.notRequired(),
  }),
}

/**
 * Update schema with the current remaining limit.
 */
export const getFullSchema = ({
  remainingLimit,
}: {
  remainingLimit: string | undefined
}) => {
  return schema.shape({
    ...basicSchema,
    fromAmount: basicSchema.fromAmount.when('mode', {
      is: WireTransferTypeEnum.withdrawal,
      then: getRemainingLimitValidation(remainingLimit),
      otherwise: (schema) => schema,
    }),
    toAmount: basicSchema.toAmount.when('mode', {
      is: WireTransferTypeEnum.deposit,
      then: getRemainingLimitValidation(remainingLimit),
      otherwise: getRailTransferLimitsValidation,
    }),
  })
}

// Used only for type inference
const _basicSchemaObject = y.object(basicSchema)

export type FxFormValues = y.InferType<typeof _basicSchemaObject>

/**
 * Function to compare two currencies and their fiat / token equivalents.
 * Returns true if the two are equivalent currencies (Ex. USDC and USD or EURC and EUR).
 */
export const isSameCurrency = (currency1: Currency, currency2: Currency) => {
  return (
    currencyToToken(currency1) === currency2 ||
    tokenToCurrency(currency1) === currency2
  )
}
